/**
 * Reader feedback on one answer (Task 251 Layer 2): «Útil» / «No útil» beside
 * Copy/Share. «Útil» sends at once; «No útil» opens an inline panel (not a
 * modal) with reason chips and an opt-in box — unchecked by default — to
 * include the question and the answer; ticking it reveals an optional note
 * that travels with them. One rating per answer; the
 * row becomes «Todá por tu opinión» and focus moves there
 * (docs/public-chat-source-feedback.md §6, §10).
 *
 * Renders inside the .answer__meta row: the buttons sit in the row, the panel
 * takes a full line below it (the row wraps).
 */
import { useEffect, useId, useRef, useState } from "react";

import type { AnswerCitation } from "../api/answer-client";
import {
  buildFeedbackPayload,
  FEEDBACK_NOTE_MAX,
  FEEDBACK_REASONS,
  postFeedback,
  type FeedbackReason,
  type FeedbackRating,
} from "../api/feedback-client";
import { rememberRating, ratedThisSession } from "../chat/feedback-store";
import type { Language, Strings } from "../strings";

type Status = "idle" | "sending" | "sent";

function ThumbGlyph(props: { down?: boolean }): JSX.Element {
  return (
    <svg className="answer__copy-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <g transform={props.down ? "rotate(180 12 12)" : undefined}>
        <path d="M7.5 11v8.5H4.5V11h3z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path
          d="M7.5 11l3.6-6.2c1.3 0 2.3 1 2 2.4L12.5 10h5.2a1.9 1.9 0 0 1 1.9 2.2l-1.1 5.8a1.9 1.9 0 0 1-1.9 1.5H7.5"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
}

export function FeedbackControls(props: {
  requestId: string;
  language: Language;
  strings: Strings;
  endpoint: string;
  question: string;
  answer: string;
  sources: readonly AnswerCitation[];
}): JSX.Element {
  const { requestId, language, strings, endpoint } = props;
  const [status, setStatus] = useState<Status>(() => (ratedThisSession(requestId) !== null ? "sent" : "idle"));
  const [panelOpen, setPanelOpen] = useState(false);
  const [justSent, setJustSent] = useState(false);
  const [reasons, setReasons] = useState<FeedbackReason[]>([]);
  const [note, setNote] = useState("");
  const [share, setShare] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ackRef = useRef<HTMLParagraphElement | null>(null);
  const noteId = useId();
  const hintId = useId();
  const shareId = useId();

  // external: DOM focus — after a rating is sent, move focus to the
  // acknowledgment so keyboard and screen-reader users hear it (§10). Only for
  // a rating sent now, never for one restored from this session.
  useEffect(() => {
    if (status === "sent" && justSent) ackRef.current?.focus();
  }, [status, justSent]);

  const reasonLabel: Record<FeedbackReason, string> = {
    incorrect_teaching: strings.feedbackReasonIncorrect,
    missing_teaching: strings.feedbackReasonMissing,
    unrelated_source: strings.feedbackReasonUnrelated,
    too_slow: strings.feedbackReasonSlow,
  };

  async function send(rating: FeedbackRating): Promise<void> {
    setStatus("sending");
    setError(null);
    const result = await postFeedback(
      endpoint,
      buildFeedbackPayload({
        requestId,
        rating,
        language,
        reasons,
        note,
        share,
        question: props.question,
        answer: props.answer,
        sources: props.sources,
      }),
    );
    if (result.ok) {
      rememberRating(requestId, rating);
      setJustSent(true);
      setStatus("sent");
      return;
    }
    setError(result.kind === "rate_limit" ? strings.feedbackRateLimited : strings.feedbackFailed);
    setStatus("idle");
  }

  if (status === "sent") {
    return (
      <p className="answer__feedback-ack" role="status" tabIndex={-1} ref={ackRef} data-testid="feedback-ack">
        {strings.feedbackThanks}
      </p>
    );
  }

  const sending = status === "sending";
  return (
    <>
      <span className="answer__feedback" role="group" aria-label={strings.feedbackGroupLabel}>
        <button
          type="button"
          className="answer__copy answer__feedback-btn"
          onClick={() => void send("useful")}
          disabled={sending || panelOpen}
          data-testid="feedback-useful"
        >
          <ThumbGlyph />
          <span className="answer__copy-label">{strings.feedbackUseful}</span>
        </button>
        <button
          type="button"
          className={`answer__copy answer__feedback-btn${panelOpen ? " answer__feedback-btn--open" : ""}`}
          onClick={() => {
            setError(null);
            setPanelOpen(!panelOpen);
          }}
          aria-expanded={panelOpen}
          disabled={sending}
          data-testid="feedback-not-useful"
        >
          <ThumbGlyph down />
          <span className="answer__copy-label">{strings.feedbackNotUseful}</span>
        </button>
      </span>
      {error !== null && !panelOpen ? (
        <span className="answer__feedback-error" role="alert">{error}</span>
      ) : null}
      {panelOpen ? (
        <form
          className="answer__feedback-panel"
          data-testid="feedback-panel"
          onSubmit={(event) => {
            event.preventDefault();
            void send("not_useful");
          }}
        >
          <fieldset className="answer__feedback-reasons">
            <legend>{strings.feedbackReasonsLegend}</legend>
            {FEEDBACK_REASONS.map((reason) => {
              const on = reasons.includes(reason);
              return (
                <button
                  key={reason}
                  type="button"
                  className={`answer__feedback-chip${on ? " answer__feedback-chip--on" : ""}`}
                  aria-pressed={on}
                  disabled={sending}
                  onClick={() => setReasons(on ? reasons.filter((r) => r !== reason) : [...reasons, reason])}
                >
                  {reasonLabel[reason]}
                </button>
              );
            })}
          </fieldset>
          <label className="answer__feedback-share" htmlFor={shareId}>
            <input id={shareId} type="checkbox" checked={share} disabled={sending} onChange={(event) => setShare(event.target.checked)} />
            <span>{strings.feedbackShareLabel}</span>
          </label>
          {/* The note travels only with the shared conversation, so it appears
              only once the reader ticks the box — never a note that is dropped. */}
          {share ? (
            <>
              <label className="answer__feedback-label" htmlFor={noteId}>{strings.feedbackNoteLabel}</label>
              <textarea
                id={noteId}
                className="answer__feedback-note"
                value={note}
                maxLength={FEEDBACK_NOTE_MAX}
                rows={3}
                aria-describedby={hintId}
                disabled={sending}
                onChange={(event) => setNote(event.target.value)}
              />
              <p id={hintId} className="answer__feedback-hint">
                {strings.feedbackNoteHint} <span aria-hidden="true">{note.length}/{FEEDBACK_NOTE_MAX}</span>
              </p>
            </>
          ) : null}
          {error !== null ? <p className="answer__feedback-error" role="alert">{error}</p> : null}
          <div className="answer__feedback-actions">
            <button type="submit" className="answer__copy answer__feedback-send" disabled={sending} data-testid="feedback-send">
              {strings.feedbackSend}
            </button>
            <button
              type="button"
              className="answer__feedback-cancel"
              disabled={sending}
              onClick={() => {
                setError(null);
                setPanelOpen(false);
              }}
            >
              {strings.feedbackCancel}
            </button>
          </div>
        </form>
      ) : null}
    </>
  );
}
